import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { leaveApi, employeeApi, compoffApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { canViewAllLeaveBalances, canExportLeaves } from '@/lib/roles';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import { Plus, Check, X, CalendarOff, Clock, FileText, Wallet, Search, Download, Ban, MessageSquare, Filter, ChevronsUpDown } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { downloadBlobFile, exportRowsToExcel, renameExcelHeaderColumns } from '@/lib/exportFile';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const isCompOffType = (type) => type === 'CompOff' || type === 'CO';

const isCompOffRecord = (leave) =>
  leave?.isCompOff === true ||
  isCompOffType(leave?.type) ||
  String(leave?.leaveId || leave?.compoffId || '').startsWith('CO');

const resolveReviewLeaveType = (leave) =>
  (isCompOffRecord(leave) ? 'CompOff' : leave?.type) || 'EL';

const normalizeCompOffAsLeave = (compoff) => {
  if (!compoff) return null;
  const id = compoff.compoffId || compoff.leaveId || compoff.id || compoff._id;
  return {
    ...compoff,
    leaveId: id,
    compoffId: id,
    type: 'CompOff',
    isCompOff: true,
  };
};

const parseCompOffBalance = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value) || 0;
  return (
    Number(value?.CompOff ?? value?.CO ?? value?.balance ?? value?.available ?? value?.count ?? 0) || 0
  );
};

const LeaveTypeBadge = ({ leave }) => (
  <Badge variant="outline">{leave?.type || '-'}</Badge>
);

const PageSizeSelect = ({ value, onValueChange, testId }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-sm text-muted-foreground">Show</span>
    <Select value={String(value)} onValueChange={(v) => onValueChange(Number(v))}>
      <SelectTrigger className="w-[70px] h-8 text-sm" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PAGE_SIZE_OPTIONS.map((size) => (
          <SelectItem key={size} value={String(size)}>
            {size}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <span className="text-sm text-muted-foreground">per page</span>
  </div>
);

const IST_DATE_OPTS = { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric' };
const IST_TIME_OPTS = { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true };

const formatIstDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString('en-IN', IST_DATE_OPTS),
    time: date.toLocaleTimeString('en-IN', IST_TIME_OPTS),
  };
};

const IstDateTimeCell = ({ value }) => {
  const formatted = formatIstDateTime(value);
  if (!formatted) return <span className="font-mono text-xs">-</span>;
  return (
    <div className="font-mono text-xs leading-tight">
      <div>{formatted.date}</div>
      <div className="text-muted-foreground">{formatted.time}</div>
    </div>
  );
};

export default function LeavePage() {
  const { user, hasAccess } = useAuth();
  const defaultTab = hasAccess('Manager') ? 'pending' : 'history';
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [myBalance, setMyBalance] = useState(null);
  const [compOffBalance, setCompOffBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);

  const [applyOpen, setApplyOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmReviewOpen, setConfirmReviewOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewType, setReviewType] = useState('EL');
  const [pendingReviewAction, setPendingReviewAction] = useState('');
  const [form, setForm] = useState({ type: 'EL', startDate: '', endDate: '', reason: '' });
  const [page, setPage] = useState(1);
  const [totalLeaves, setTotalLeaves] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [historyFilters, setHistoryFilters] = useState({
    appliedFrom: '',
    appliedTo: '',
    leaveFrom: '',
    leaveTo: '',
  });
  const [historyEmpFilter, setHistoryEmpFilter] = useState('all');
  const [historyEmpFilterOpen, setHistoryEmpFilterOpen] = useState(false);
  const [allEmployeesForFilter, setAllEmployeesForFilter] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [balancesPageSize, setBalancesPageSize] = useState(10);
  const [balancesPage, setBalancesPage] = useState(1);
  const [totalBalanceEmployees, setTotalBalanceEmployees] = useState(0);
  const [totalBalancePages, setTotalBalancePages] = useState(1);
  const [balanceSearch, setBalanceSearch] = useState('');
  const [debouncedBalanceSearch, setDebouncedBalanceSearch] = useState('');
  const [exportingHistory, setExportingHistory] = useState(false);
  const [exportingBalances, setExportingBalances] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [selectedLeaveForCancel, setSelectedLeaveForCancel] = useState(null);
  const [cancelComment, setCancelComment] = useState('');
  const [cancellingLeave, setCancellingLeave] = useState(false);

  const isEmployee = user?.role === 'Employee';
  const isManager = user?.role === 'Manager';
  const canViewAllBalances = canViewAllLeaveBalances(user?.role);
  const canExportLeaveHistory = canExportLeaves(user?.role);

  const isOwnLeave = (leave) => {
    const leaveEmployeeId = leave?.employeeId;
    const userEmployeeId = user?.employeeId;
    if (!leaveEmployeeId || !userEmployeeId) return false;
    return String(leaveEmployeeId) === String(userEmployeeId);
  };

  const showReviewActions = !isManager;

  const canShowReviewButton = (leave) => {
    if (isManager) return false;
    if (hasAccess('HR')) return true;
    if (isEmployee) return !isOwnLeave(leave);
    return false;
  };

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };

  const normalizeLeaveListResponse = (value) => {
    if (Array.isArray(value)) {
      return { leaves: value, total: value.length, page: 1, pages: 1 };
    }
    if (Array.isArray(value?.leaves)) {
      return {
        leaves: value.leaves,
        total: value?.total ?? value.leaves.length,
        page: value?.page ?? 1,
        pages: value?.pages ?? 1,
      };
    }
    if (Array.isArray(value?.items)) {
      return {
        leaves: value.items,
        total: value?.total ?? value.items.length,
        page: value?.page ?? 1,
        pages: value?.pages ?? 1,
      };
    }
    return { leaves: [], total: 0, page: 1, pages: 1 };
  };

  const normalizeEmployeeListResponse = (value) => {
    if (Array.isArray(value)) {
      return { employees: value, total: value.length, page: 1, pages: 1 };
    }
    if (Array.isArray(value?.items)) {
      return {
        employees: value.items,
        total: value?.total ?? value.items.length,
        page: value?.page ?? 1,
        pages: value?.pages ?? 1,
      };
    }
    if (Array.isArray(value?.employees)) {
      return {
        employees: value.employees,
        total: value?.total ?? value.employees.length,
        page: value?.page ?? 1,
        pages: value?.pages ?? 1,
      };
    }
    return { employees: [], total: 0, page: 1, pages: 1 };
  };

  useEffect(() => {
    if (isEmployee) return;
    const fetchFilterEmployees = async () => {
      try {
        const emps = await employeeApi.getAll({ limit: 1000, status: 'Active' }).catch(() => []);
        const normalized = normalizeEmployeeListResponse(emps);
        setAllEmployeesForFilter(normalized.employees);
      } catch {
        setAllEmployeesForFilter([]);
      }
    };
    fetchFilterEmployees();
  }, [isEmployee]);

  useEffect(() => {
    const params = { page, limit: pageSize };
    if (activeTab === 'history') {
      if (historyFilters?.appliedFrom) params.appliedFrom = historyFilters.appliedFrom;
      if (historyFilters?.appliedTo) params.appliedTo = historyFilters.appliedTo;
      if (historyFilters?.leaveFrom) params.leaveFrom = historyFilters.leaveFrom;
      if (historyFilters?.leaveTo) params.leaveTo = historyFilters.leaveTo;
      if (!isEmployee && historyEmpFilter !== 'all') params.employeeId = historyEmpFilter;
    }

    const fetchData = async () => {
      try {
        setTableLoading(true);
        const [leaveResponse, compoffResponse] = await Promise.all([
          leaveApi.getAll(params).catch(() => []),
          compoffApi.getAll(params).catch(() => []),
        ]);
        const normalizedLeaveResponse = normalizeLeaveListResponse(leaveResponse);
        const compoffItems = normalizeArrayResponse(compoffResponse)
          .map(normalizeCompOffAsLeave)
          .filter(Boolean);
        const alsoPagedCompoffs = normalizeLeaveListResponse(compoffResponse).leaves
          .map(normalizeCompOffAsLeave)
          .filter(Boolean);
        const compoffs =
          compoffItems.length > 0
            ? compoffItems
            : alsoPagedCompoffs;

        const merged = [...normalizedLeaveResponse.leaves, ...compoffs].sort((a, b) => {
          const aTime = new Date(a?.appliedOn || a?.createdAt || 0).getTime();
          const bTime = new Date(b?.appliedOn || b?.createdAt || 0).getTime();
          return bTime - aTime;
        });

        setLeaves(merged);
        const compoffTotal =
          Array.isArray(compoffResponse)
            ? compoffResponse.length
            : Number(compoffResponse?.total ?? compoffs.length) || compoffs.length;
        const total = (normalizedLeaveResponse.total || 0) + (compoffTotal || 0);
        setTotalLeaves(total);
        const pages = Math.max(
          normalizedLeaveResponse.pages || 1,
          Math.ceil(total / pageSize) || 1
        );
        setTotalPages(pages);
        setPage((p) => clampPageToTotal(p, pages));

        if (user?.employeeId) {
          const [empData, cofBalance] = await Promise.all([
            employeeApi.getById(user.employeeId).catch(() => null),
            compoffApi.getBalance(user.employeeId).catch(() => null),
          ]);
          setMyBalance(empData?.leaveBalance ?? null);
          setCompOffBalance(parseCompOffBalance(cofBalance));
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setTableLoading(false);
        setLoading(false);
      }
    };
    fetchData();
  }, [user, page, pageSize, activeTab, historyFilters, historyEmpFilter, isEmployee]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedBalanceSearch(balanceSearch);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [balanceSearch]);

  useEffect(() => {
    setBalancesPage(1);
  }, [debouncedBalanceSearch]);

  useEffect(() => {
    if (!canViewAllBalances || activeTab !== 'history') return;

    const fetchBalanceEmployees = async () => {
      try {
        setBalancesLoading(true);
        const params = {
          page: balancesPage,
          limit: balancesPageSize,
        };
        const trimmedSearch = debouncedBalanceSearch?.trim?.();
        if (trimmedSearch) params.search = trimmedSearch;

        const response = await employeeApi.getAll(params);
        const normalized = normalizeEmployeeListResponse(response);
        setEmployees(normalized.employees);
        setTotalBalanceEmployees(normalized.total);
        setTotalBalancePages(normalized.pages);
        setBalancesPage((p) => clampPageToTotal(p, normalized.pages));
      } catch (err) {
        console.error('Failed to fetch employee balances:', err);
        setEmployees([]);
        setTotalBalanceEmployees(0);
        setTotalBalancePages(1);
      } finally {
        setBalancesLoading(false);
      }
    };

    fetchBalanceEmployees();
  }, [canViewAllBalances, activeTab, balancesPage, balancesPageSize, debouncedBalanceSearch]);

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleClearHistoryFilters = () => {
    setHistoryFilters({
      appliedFrom: '',
      appliedTo: '',
      leaveFrom: '',
      leaveTo: '',
    });
    setHistoryEmpFilter('all');
    setPage(1);
  };

  const selectedHistoryEmpLabel = useMemo(() => {
    if (historyEmpFilter === 'all') return 'All Employees';
    const emp = allEmployeesForFilter.find((e) => (e?.empId || e?.id) === historyEmpFilter);
    if (!emp) return historyEmpFilter;
    return `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || historyEmpFilter;
  }, [historyEmpFilter, allEmployeesForFilter]);

  const getHistoryExportParams = () => {
    const params = {};
    if (historyFilters?.appliedFrom) params.appliedFrom = historyFilters.appliedFrom;
    if (historyFilters?.appliedTo) params.appliedTo = historyFilters.appliedTo;
    if (historyFilters?.leaveFrom) params.leaveFrom = historyFilters.leaveFrom;
    if (historyFilters?.leaveTo) params.leaveTo = historyFilters.leaveTo;
    if (!isEmployee && historyEmpFilter !== 'all') params.employeeId = historyEmpFilter;
    return params;
  };

  const handleExportHistory = async () => {
    if (exportingHistory) return;
    setExportingHistory(true);
    try {
      const response = await leaveApi.export(getHistoryExportParams());
      const blob = await response.blob();
      const renamedBlob = await renameExcelHeaderColumns(blob, {
        'Reviewed By': 'Approved By',
        reviewedBy: 'Approved By',
        reviewed_by: 'Approved By',
      });
      const disposition = response.headers.get('content-disposition') || '';
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const fileName = matchedFileName || `leave_history_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlobFile(renamedBlob, fileName);
      toast.success('Leave history exported');
    } catch (err) {
      toast.error(err?.message || 'Failed to export leave history');
    } finally {
      setExportingHistory(false);
    }
  };

  const handleExportLeaveBalances = async () => {
    if (exportingBalances) return;
    setExportingBalances(true);
    try {
      const allEmps = await employeeApi.getAllForExport({ status: 'Active' });

      if (!allEmps?.length) {
        toast.error('No leave balance records to export');
        return;
      }

      const header = ['Employee ID', 'Employee Name', 'EL', 'CL', 'ML', 'LOP'];
      const rows = allEmps.map((emp) => {
        const empId = emp?.empId || emp?.id || '';
        const name = `${emp?.firstName || ''} ${emp?.lastName || ''}`.trim();
        const bal = emp?.leaveBalance;
        return {
          'Employee ID': empId,
          'Employee Name': name,
          EL: bal?.EL ?? 0,
          CL: bal?.CL ?? 0,
          ML: bal?.ML ?? 0,
          LOP: bal?.LOP ?? 0,
        };
      });

      exportRowsToExcel(rows, {
        fileName: `leave_balances_${new Date().toISOString().slice(0, 10)}`,
        sheetName: 'Leave Balances',
        header,
      });
      toast.success(`Leave balances exported (${rows.length} employees)`);
    } catch (err) {
      toast.error(err?.message || 'Failed to export leave balances');
    } finally {
      setExportingBalances(false);
    }
  };

  const getEmployeeBalance = (employeeId) => {
    if (isEmployee && myBalance && employeeId === user?.employeeId) return myBalance;
    const emp = employees.find((e) => (e?.empId || e?.id) === employeeId);
    return emp?.leaveBalance ?? { EL: 0, CL: 0, ML: 0, LOP: 0 };
  };

  const displayLeaves = leaves;

  const showEmployeeColumn = useMemo(() => {
    if (!isEmployee) return true;
    return leaves.some((l) => l?.employeeId && l.employeeId !== user?.employeeId);
  }, [leaves, isEmployee, user?.employeeId]);

  const historyColSpan = showEmployeeColumn ? 11 : 10;
  const pendingColSpan = 6 + (showEmployeeColumn ? 1 : 0) + (showReviewActions ? 1 : 0);

  const pendingLeaves = useMemo(() => {
    return leaves.filter((l) => l.status === 'Pending');
  }, [leaves]);

  const handleApply = async () => {
    if (!form.startDate || !form.endDate || !form.reason) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      let newLeave;
      if (form.type === 'CompOff') {
        const created = await compoffApi.apply({
          startDate: form.startDate,
          endDate: form.endDate,
          reason: form.reason,
        });
        newLeave = normalizeCompOffAsLeave(created);
      } else {
        newLeave = await leaveApi.apply({
          type: form.type,
          startDate: form.startDate,
          endDate: form.endDate,
          reason: form.reason,
        });
      }
      setLeaves((prev) => [newLeave, ...prev]);
      toast.success(form.type === 'CompOff' ? 'CompOff application submitted' : 'Leave application submitted');
      setApplyOpen(false);
      setForm({ type: 'EL', startDate: '', endDate: '', reason: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to apply for leave');
    }
  };

  const handleOpenReview = (leave) => {
    if (!leave) return;
    setSelectedLeave(leave);
    setReviewType(resolveReviewLeaveType(leave));
    setReviewComment('');
    setReviewOpen(true);
  };

  const handleReview = async (action) => {
    if (!selectedLeave) return;
    if (action === 'Rejected' && !reviewComment?.trim()) {
      toast.error('Comment is required when rejecting a leave request');
      return;
    }
    try {
      const leaveId = selectedLeave.leaveId || selectedLeave.compoffId || selectedLeave.id;
      const reviewingAsCompOff = isCompOffType(reviewType);
      let updated;
      if (isCompOffRecord(selectedLeave) && reviewingAsCompOff) {
        updated = normalizeCompOffAsLeave(
          await compoffApi.review(leaveId, {
            status: action,
            reviewComment,
          })
        );
      } else {
        updated = await leaveApi.review(leaveId, {
          status: action,
          reviewComment,
          type: reviewType,
        });
        if (reviewingAsCompOff) updated = normalizeCompOffAsLeave(updated);
      }
      setLeaves((prev) =>
        prev.map((l) =>
          (l.leaveId || l.id) === leaveId ? { ...l, ...updated } : l
        )
      );
      toast.success(`${isCompOffRecord(selectedLeave) ? 'CompOff' : 'Leave'} ${action.toLowerCase()}`);
      setReviewOpen(false);
      setSelectedLeave(null);
      setReviewComment('');
      setReviewType('EL');
    } catch (err) {
      toast.error(err.message || 'Failed to review leave');
    }
  };

  const handleOpenReviewConfirm = (action) => {
    if (!selectedLeave) return;
    if (action === 'Rejected' && !reviewComment?.trim()) {
      toast.error('Comment is required when rejecting a leave request');
      return;
    }
    setPendingReviewAction(action);
    setConfirmReviewOpen(true);
  };

  const handleConfirmReview = async () => {
    if (!pendingReviewAction) return;
    await handleReview(pendingReviewAction);
    setConfirmReviewOpen(false);
    setPendingReviewAction('');
  };

  const canCancelLeave = (leave) => {
    const status = leave?.status;
    if (status !== 'Pending' && status !== 'Approved') return false;
    if (isOwnLeave(leave)) return false;
    if (isEmployee) return false;
    return true;
  };

  const handleOpenCancel = (leave) => {
    if (!canCancelLeave(leave)) return;
    setSelectedLeaveForCancel(leave);
    setCancelComment('');
    setCancelOpen(true);
  };

  const handleCancelLeave = async () => {
    if (!selectedLeaveForCancel || cancellingLeave) return;
    const leaveId = selectedLeaveForCancel.leaveId || selectedLeaveForCancel.id;
    if (!leaveId) return;

    setCancellingLeave(true);
    try {
      const updated = isCompOffRecord(selectedLeaveForCancel)
        ? normalizeCompOffAsLeave(await compoffApi.cancel(leaveId, cancelComment))
        : await leaveApi.cancel(leaveId, cancelComment);
      setLeaves((prev) =>
        prev.map((l) => ((l.leaveId || l.id) === leaveId ? { ...l, ...updated } : l))
      );
      toast.success(isCompOffRecord(selectedLeaveForCancel) ? 'CompOff cancelled' : 'Leave cancelled');
      setCancelOpen(false);
      setSelectedLeaveForCancel(null);
      setCancelComment('');
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel leave');
    } finally {
      setCancellingLeave(false);
    }
  };

  const statusColor = (status) => {
    if (status === 'Approved') return 'bg-green-100 text-green-700 border-green-200';
    if (status === 'Pending') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    if (status === 'Cancelled') return 'bg-gray-100 text-gray-700 border-gray-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="leave-page" className="space-y-3 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-[Manrope]">Leave Management</h1>
          <p className="text-muted-foreground mt-1">Apply, track, and manage leaves ({totalLeaves || leaves.length} total)</p>
        </div>
        {isEmployee && (
          <Button onClick={() => setApplyOpen(true)} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="apply-leave-btn">
            <Plus className="w-4 h-4 mr-2" /> Apply Leave
          </Button>
        )}
      </div>

      {/* Leave Balance (for employees) */}
      {isEmployee && (myBalance || compOffBalance != null) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Earned Leave</p>
              <p className="text-3xl font-bold font-mono mt-1 text-[#0047AB]">{myBalance?.EL || 0}</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Casual Leave</p>
              <p className="text-3xl font-bold font-mono mt-1 text-[#6366f1]">{myBalance?.CL || 0}</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Medical Leave</p>
              <p className="text-3xl font-bold font-mono mt-1 text-[#10b981]">{myBalance?.ML || 0}</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">LOP Taken</p>
              <p className="text-3xl font-bold font-mono mt-1 text-[#ef4444]">{myBalance?.LOP || 0}</p>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">CompOff</p>
              <p className="text-3xl font-bold font-mono mt-1 text-[#f59e0b]">{compOffBalance || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          setPage(1);
        }}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending Approvals
            {pendingLeaves.length > 0 && (
              <Badge className="ml-2 bg-yellow-100 text-yellow-700 border-yellow-200 text-xs">{pendingLeaves.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Leave History</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
            <Card className="border border-border shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      {showEmployeeColumn && <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>}
                      <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">From</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">To</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Reason</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-center">Balance</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Applied On</TableHead>
                      {showReviewActions && <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableLoading && (
                      <TableRow>
                        <TableCell colSpan={pendingColSpan} className="text-center py-6 text-muted-foreground">
                          Loading leaves...
                        </TableCell>
                      </TableRow>
                    )}
                    {!tableLoading && pendingLeaves.map(leave => {
                      const bal = leave?.leaveBalance ?? getEmployeeBalance(leave?.employeeId);
                      return (
                      <TableRow key={leave.leaveId || leave.id} data-testid={`pending-leave-${leave.leaveId || leave.id}`}>
                        {showEmployeeColumn && (
                          <TableCell className="text-sm font-medium">
                            {leave?.employeeId ? (
                              <Link
                                to={`/employees/${leave.employeeId}`}
                                className="hover:text-[#0047AB] hover:underline"
                                data-testid={`pending-employee-link-${leave.employeeId}`}
                              >
                                {leave.employeeName}
                              </Link>
                            ) : (
                              leave.employeeName
                            )}
                          </TableCell>
                        )}
                        <TableCell><LeaveTypeBadge leave={leave} /></TableCell>
                        <TableCell className="font-mono text-xs">{leave.startDate}</TableCell>
                        <TableCell className="font-mono text-xs">{leave.endDate}</TableCell>
                        <TableCell className="text-sm max-w-[160px]">
                          {leave?.reason ? (
                            <HoverCard openDelay={150} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <button
                                  type="button"
                                  className="max-w-full truncate cursor-help outline-none text-left"
                                  aria-label="View full reason"
                                  data-testid={`pending-leave-reason-${leave.leaveId || leave.id}`}
                                >
                                  {leave.reason}
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent align="start" className="w-72 p-3 space-y-1.5">
                                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason</p>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{leave.reason}</p>
                              </HoverCardContent>
                            </HoverCard>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-center" data-testid={`pending-balance-${leave.leaveId || leave.id}`}>
                          <div className="text-xs">
                            <span className="text-[#0047AB] font-mono">EL:{bal?.EL ?? 0}</span>
                            <span className="text-muted-foreground mx-1">|</span>
                            <span className="text-[#6366f1] font-mono">CL:{bal?.CL ?? 0}</span>
                            <span className="text-muted-foreground mx-1">|</span>
                            <span className="text-green-600 font-mono">ML:{bal?.ML ?? 0}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{new Date(leave.appliedOn).toLocaleDateString()}</TableCell>
                        {showReviewActions && (
                          <TableCell>
                            {canShowReviewButton(leave) && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => handleOpenReview(leave)}
                                  data-testid={`review-leave-${leave.leaveId || leave.id}`}
                                >
                                  Review
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })}
                    {!tableLoading && pendingLeaves.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={pendingColSpan} className="text-center py-8 text-muted-foreground">
                          <CalendarOff className="w-6 h-6 mx-auto mb-2 opacity-50" />
                          No pending leave requests
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
                <PageSizeSelect
                  value={pageSize}
                  onValueChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  testId="leave-pending-page-size-select"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page <= 1}
                  data-testid="leave-pending-prev-page-btn"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page >= totalPages}
                  data-testid="leave-pending-next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          </TabsContent>

        <TabsContent value="history">
          {/* Leave Balance Overview for Managers/Admins */}
          {!isEmployee && (
            <Card className="border border-border shadow-sm mb-4" data-testid="all-leave-balances-card">
              <CardHeader className="pb-2">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-sm font-[Manrope] flex flex-wrap items-center gap-2">
                    <Wallet className="w-4 h-4 text-[#0047AB]" /> Employee Leave Balances
                    <span className="text-xs font-normal text-muted-foreground">
                      ({totalBalanceEmployees} total)
                    </span>
                  </CardTitle>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:ml-auto w-full sm:w-auto">
                  <div className="relative w-full sm:max-w-sm">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="balance-employee-search"
                      placeholder="Search by name, ID, or phone..."
                      value={balanceSearch}
                      onChange={(e) => setBalanceSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleExportLeaveBalances}
                    disabled={exportingBalances || balancesLoading}
                    data-testid="leave-balances-export-btn"
                    className="shrink-0"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exportingBalances ? 'Exporting...' : 'Export Balances'}
                  </Button>
                </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-center">EL</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-center">CL</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-center">ML</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-center">LOP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {balancesLoading && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                            Loading employee balances...
                          </TableCell>
                        </TableRow>
                      )}
                      {!balancesLoading && employees.map((emp) => {
                        const empId = emp?.empId || emp?.id;
                        const bal = emp?.leaveBalance;
                        return (
                          <TableRow key={empId} data-testid={`balance-row-${empId}`}>
                            <TableCell className="text-sm">
                              <span className="font-medium">{emp?.firstName} {emp?.lastName}</span>
                              <span className="text-xs text-muted-foreground font-mono ml-2">{empId}</span>
                            </TableCell>
                            <TableCell className="text-center font-mono font-bold text-[#0047AB]">{bal?.EL ?? 0}</TableCell>
                            <TableCell className="text-center font-mono font-bold text-[#6366f1]">{bal?.CL ?? 0}</TableCell>
                            <TableCell className="text-center font-mono font-bold text-[#10b981]">{bal?.ML ?? 0}</TableCell>
                            <TableCell className="text-center font-mono font-bold text-[#ef4444]">{bal?.LOP ?? 0}</TableCell>
                          </TableRow>
                        );
                      })}
                      {!balancesLoading && employees.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                            No employee balance records
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
          {!isEmployee && (
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  Page {balancesPage} of {totalBalancePages}
                </p>
                <PageSizeSelect
                  value={balancesPageSize}
                  onValueChange={(size) => {
                    setBalancesPageSize(size);
                    setBalancesPage(1);
                  }}
                  testId="balances-page-size-select"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBalancesPage((prev) => Math.max(prev - 1, 1))}
                  disabled={balancesPage <= 1 || balancesLoading}
                  data-testid="balances-prev-page-btn"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBalancesPage((prev) => Math.min(prev + 1, totalBalancePages))}
                  disabled={balancesPage >= totalBalancePages || balancesLoading}
                  data-testid="balances-next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          <Card className="border border-border shadow-sm mb-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end gap-3 sm:gap-4">
                {!isEmployee && (
                  <div className="space-y-1.5 w-full sm:w-[220px]">
                    <Label className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Employee</Label>
                    <Popover open={historyEmpFilterOpen} onOpenChange={setHistoryEmpFilterOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={historyEmpFilterOpen}
                          className="w-full justify-between font-normal"
                          data-testid="leave-history-employee-filter"
                        >
                          <span className="flex items-center gap-2 truncate">
                            <Filter className="w-3 h-3 shrink-0" />
                            <span className="truncate">{selectedHistoryEmpLabel}</span>
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[280px] p-0"
                        align="start"
                        side="bottom"
                        avoidCollisions={false}
                      >
                        <Command>
                          <CommandInput placeholder="Search employee..." />
                          <CommandList>
                            <CommandEmpty>No employee found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="all-employees"
                                onSelect={() => {
                                  setHistoryEmpFilter('all');
                                  setPage(1);
                                  setHistoryEmpFilterOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    historyEmpFilter === 'all' ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                                All Employees
                              </CommandItem>
                              {allEmployeesForFilter.map((e) => {
                                const empId = e?.empId || e?.id;
                                const label = `${e?.firstName || ''} ${e?.lastName || ''}`.trim();
                                return (
                                  <CommandItem
                                    key={empId}
                                    value={`${label} ${empId}`}
                                    onSelect={() => {
                                      setHistoryEmpFilter(empId);
                                      setPage(1);
                                      setHistoryEmpFilterOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        historyEmpFilter === empId ? 'opacity-100' : 'opacity-0'
                                      )}
                                    />
                                    <span className="truncate">{label}</span>
                                    <span className="ml-auto text-xs text-muted-foreground font-mono pl-2">
                                      {empId}
                                    </span>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                <div className="space-y-1.5 w-full sm:w-[160px]">
                  <Label htmlFor="leave-filter-applied-from" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Applied From</Label>
                  <Input
                    id="leave-filter-applied-from"
                    type="date"
                    value={historyFilters.appliedFrom}
                    onChange={(e) => handleHistoryFilterChange('appliedFrom', e.target.value)}
                    data-testid="leave-history-applied-from"
                  />
                </div>
                <div className="space-y-1.5 w-full sm:w-[160px]">
                  <Label htmlFor="leave-filter-applied-to" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Applied To</Label>
                  <Input
                    id="leave-filter-applied-to"
                    type="date"
                    value={historyFilters.appliedTo}
                    onChange={(e) => handleHistoryFilterChange('appliedTo', e.target.value)}
                    data-testid="leave-history-applied-to"
                  />
                </div>
                <div className="space-y-1.5 w-full sm:w-[160px]">
                  <Label htmlFor="leave-filter-leave-from" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Leave From</Label>
                  <Input
                    id="leave-filter-leave-from"
                    type="date"
                    value={historyFilters.leaveFrom}
                    onChange={(e) => handleHistoryFilterChange('leaveFrom', e.target.value)}
                    data-testid="leave-history-leave-from"
                  />
                </div>
                <div className="space-y-1.5 w-full sm:w-[160px]">
                  <Label htmlFor="leave-filter-leave-to" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Leave To</Label>
                  <Input
                    id="leave-filter-leave-to"
                    type="date"
                    value={historyFilters.leaveTo}
                    onChange={(e) => handleHistoryFilterChange('leaveTo', e.target.value)}
                    data-testid="leave-history-leave-to"
                  />
                </div>
                <div className="flex flex-row items-center gap-2 pb-0.5">
                  {canExportLeaveHistory && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleExportHistory}
                      disabled={exportingHistory}
                      data-testid="leave-history-export-btn"
                      className="shrink-0"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {exportingHistory ? 'Exporting...' : 'Export'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClearHistoryFilters}
                    data-testid="leave-history-clear-filters-btn"
                    className="shrink-0 px-3 text-sm"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    {showEmployeeColumn && <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>}
                    <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">From</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">To</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Reason</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Approved By</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Approved On</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-center">Balance</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Applied</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableLoading && (
                    <TableRow>
                      <TableCell colSpan={historyColSpan} className="text-center py-6 text-muted-foreground">
                        Loading leaves...
                      </TableCell>
                    </TableRow>
                  )}
                  {!tableLoading && displayLeaves.sort((a, b) => new Date(b.appliedOn) - new Date(a.appliedOn)).map(leave => {
                    const bal = leave?.leaveBalance ?? getEmployeeBalance(leave?.employeeId);
                    return (
                    <TableRow key={leave.leaveId || leave.id} data-testid={`leave-row-${leave.leaveId || leave.id}`}>
                      {showEmployeeColumn && (
                        <TableCell className="text-sm">
                          {leave?.employeeId ? (
                            <Link
                              to={`/employees/${leave.employeeId}`}
                              className="hover:text-[#0047AB] hover:underline"
                              data-testid={`history-employee-link-${leave.employeeId}`}
                            >
                              {leave.employeeName}
                            </Link>
                          ) : (
                            leave.employeeName
                          )}
                        </TableCell>
                      )}
                      <TableCell><LeaveTypeBadge leave={leave} /></TableCell>
                      <TableCell className="font-mono text-xs">{leave.startDate}</TableCell>
                      <TableCell className="font-mono text-xs">{leave.endDate}</TableCell>
                      <TableCell className="text-sm max-w-[160px]">
                        {leave?.reason ? (
                          <HoverCard openDelay={150} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <button
                                type="button"
                                className="max-w-full truncate cursor-help outline-none text-left"
                                aria-label="View full reason"
                                data-testid={`leave-reason-${leave.leaveId || leave.id}`}
                              >
                                {leave.reason}
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent align="start" className="w-72 p-3 space-y-1.5">
                              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason</p>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{leave.reason}</p>
                            </HoverCardContent>
                          </HoverCard>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{leave?.reviewedByName ?? leave?.reviewedBy ?? '-'}</TableCell>
                      <TableCell><IstDateTimeCell value={leave?.reviewedOn} /></TableCell>
                      <TableCell>
                        {leave?.reviewComment ? (
                          <HoverCard openDelay={150} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 cursor-help outline-none"
                                aria-label={`Status ${leave.status}, view review comment`}
                                data-testid={`leave-status-comment-${leave.leaveId || leave.id}`}
                              >
                                <Badge className={statusColor(leave.status)}>{leave.status}</Badge>
                                <MessageSquare className="w-3 h-3 text-muted-foreground" />
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent align="start" className="w-72 p-3 space-y-1.5">
                              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Review Comment</p>
                              <p className="text-sm leading-relaxed">{leave.reviewComment}</p>
                              {leave?.reviewedByName && (
                                <p className="text-xs text-muted-foreground pt-1 border-t border-border">
                                  — {leave.reviewedByName}
                                </p>
                              )}
                            </HoverCardContent>
                          </HoverCard>
                        ) : (
                          <Badge className={statusColor(leave.status)}>{leave.status}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center" data-testid={`leave-balance-${leave.leaveId || leave.id}`}>
                        <div className="text-xs">
                          <span className="text-[#0047AB] font-mono">EL:{bal?.EL ?? 0}</span>
                          <span className="text-muted-foreground mx-1">|</span>
                          <span className="text-[#6366f1] font-mono">CL:{bal?.CL ?? 0}</span>
                          <span className="text-muted-foreground mx-1">|</span>
                          <span className="text-green-600 font-mono">ML:{bal?.ML ?? 0}</span>
                        </div>
                      </TableCell>
                      <TableCell><IstDateTimeCell value={leave?.appliedOn} /></TableCell>
                      <TableCell>
                        {canCancelLeave(leave) ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-red-500 hover:text-red-700"
                            onClick={() => handleOpenCancel(leave)}
                            data-testid={`cancel-leave-${leave.leaveId || leave.id}`}
                          >
                            <Ban className="w-3 h-3 mr-1" /> Cancel
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {!tableLoading && displayLeaves.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={historyColSpan} className="text-center py-8 text-muted-foreground">
                        No leave records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <PageSizeSelect
                value={pageSize}
                onValueChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                testId="leave-history-page-size-select"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page <= 1}
                data-testid="leave-history-prev-page-btn"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page >= totalPages}
                data-testid="leave-history-next-page-btn"
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Apply Leave Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Apply for Leave</DialogTitle>
            <DialogDescription>Fill in the details for your leave request</DialogDescription>
          </DialogHeader>
          {/* Leave Balance Summary in Application */}
          {(myBalance || compOffBalance != null) && (
            <div data-testid="apply-leave-balance" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-3 rounded-lg bg-muted/50 border border-border">
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">EL Balance</p>
                <p className="text-lg font-bold font-mono text-[#0047AB]">{myBalance?.EL || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">CL Balance</p>
                <p className="text-lg font-bold font-mono text-[#6366f1]">{myBalance?.CL || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">ML Balance</p>
                <p className="text-lg font-bold font-mono text-[#10b981]">{myBalance?.ML || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">LOP Taken</p>
                <p className="text-lg font-bold font-mono text-[#ef4444]">{myBalance?.LOP || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">CompOff</p>
                <p className="text-lg font-bold font-mono text-[#f59e0b]">{compOffBalance || 0}</p>
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Leave Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EL">Earned Leave (EL)</SelectItem>
                  <SelectItem value="CL">Casual Leave (CL)</SelectItem>
                  <SelectItem value="ML">Medical Leave (ML)</SelectItem>
                  <SelectItem value="LOP">Loss of Pay (LOP)</SelectItem>
                  <SelectItem value="ONDUTY">On Duty (ONDUTY)</SelectItem>
                  <SelectItem value="CompOff">Compensatory Off (CompOff)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Date</Label>
                <Input type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} data-testid="leave-start-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">End Date</Label>
                <Input type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} data-testid="leave-end-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason</Label>
              <Textarea value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="Describe your reason..." data-testid="leave-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button onClick={handleApply} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="submit-leave-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Leave Dialog */}
      <Dialog
        open={reviewOpen}
        onOpenChange={(open) => {
          setReviewOpen(open);
          if (!open) {
            setSelectedLeave(null);
            setReviewComment('');
            setReviewType('EL');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Review Leave Request</DialogTitle>
            <DialogDescription>Approve or reject this leave application</DialogDescription>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Employee:</span> <strong>{selectedLeave.employeeName}</strong></div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Leave Type</Label>
                  <Select value={reviewType} onValueChange={setReviewType}>
                    <SelectTrigger className="mt-1" data-testid="review-leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EL">Earned Leave (EL)</SelectItem>
                      <SelectItem value="CL">Casual Leave (CL)</SelectItem>
                      <SelectItem value="ML">Medical Leave (ML)</SelectItem>
                      <SelectItem value="LOP">Loss of Pay (LOP)</SelectItem>
                      <SelectItem value="ONDUTY">On Duty (ONDUTY)</SelectItem>
                      <SelectItem value="CompOff">Compensatory Off (CompOff)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><span className="text-muted-foreground">From:</span> <strong className="font-mono">{selectedLeave.startDate}</strong></div>
                <div><span className="text-muted-foreground">To:</span> <strong className="font-mono">{selectedLeave.endDate}</strong></div>
              </div>
              <div className="text-sm"><span className="text-muted-foreground">Reason:</span> {selectedLeave.reason}</div>
              {/* Employee's Leave Balance for Reviewer */}
              {(() => {
                const bal = selectedLeave?.leaveBalance ?? getEmployeeBalance(selectedLeave?.employeeId);
                return (
                  <div data-testid="review-leave-balance" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">EL Balance</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'EL' ? 'text-[#0047AB]' : 'text-muted-foreground'}`}>{bal?.EL || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">CL Balance</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'CL' ? 'text-[#6366f1]' : 'text-muted-foreground'}`}>{bal?.CL || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">ML Balance</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'ML' ? 'text-[#10b981]' : 'text-muted-foreground'}`}>{bal?.ML || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">LOP Taken</p>
                      <p className={`text-lg font-bold font-mono ${reviewType === 'LOP' ? 'text-[#ef4444]' : 'text-muted-foreground'}`}>{bal?.LOP || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">CompOff</p>
                      <p className={`text-lg font-bold font-mono ${isCompOffType(reviewType) ? 'text-[#f59e0b]' : 'text-muted-foreground'}`}>
                        {selectedLeave?.leaveBalance?.CompOff ?? selectedLeave?.compOffBalance ?? compOffBalance ?? 0}
                      </p>
                    </div>
                  </div>
                );
              })()}
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Comment</Label>
                <Textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder="Add a comment..." data-testid="review-comment" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleOpenReviewConfirm('Rejected')} data-testid="reject-leave-btn">
              <X className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button onClick={() => handleOpenReviewConfirm('Approved')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-leave-btn">
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Leave Review Dialog */}
      <Dialog open={confirmReviewOpen} onOpenChange={setConfirmReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {pendingReviewAction === 'Approved' ? 'Approve Leave Request?' : 'Reject Leave Request?'}
            </DialogTitle>
            <DialogDescription>
              {pendingReviewAction === 'Approved'
                ? 'Please confirm you want to approve this leave request.'
                : 'Please confirm you want to reject this leave request.'}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-1">
            <p><span className="text-muted-foreground">Employee:</span> <strong>{selectedLeave?.employeeName || '-'}</strong></p>
            <p><span className="text-muted-foreground">Type:</span> <strong>{reviewType || '-'}</strong></p>
            {reviewType !== resolveReviewLeaveType(selectedLeave) && (
              <p><span className="text-muted-foreground">Originally applied as:</span> <strong>{selectedLeave?.type || '-'}</strong></p>
            )}
            <p><span className="text-muted-foreground">Dates:</span> <span className="font-mono">{selectedLeave?.startDate || '-'} to {selectedLeave?.endDate || '-'}</span></p>
            {pendingReviewAction === 'Rejected' && (
              <p><span className="text-muted-foreground">Comment:</span> {reviewComment?.trim() || '-'}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReviewOpen(false)}>Go Back</Button>
            <Button
              onClick={handleConfirmReview}
              className={pendingReviewAction === 'Approved' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-review-leave-btn"
            >
              {pendingReviewAction === 'Approved' ? 'Confirm Approve' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Cancel Leave Dialog */}
      <Dialog
        open={cancelOpen}
        onOpenChange={(open) => {
          setCancelOpen(open);
          if (!open) {
            setSelectedLeaveForCancel(null);
            setCancelComment('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Cancel Leave?</DialogTitle>
            <DialogDescription>
              This will cancel the leave request. You can optionally add a reason.
            </DialogDescription>
          </DialogHeader>
          {selectedLeaveForCancel && (
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-1">
                <p><span className="text-muted-foreground">ID:</span> <span className="font-mono">{selectedLeaveForCancel.leaveId || selectedLeaveForCancel.id}</span></p>
                {!isEmployee && (
                  <p><span className="text-muted-foreground">Employee:</span> <strong>{selectedLeaveForCancel.employeeName || '-'}</strong></p>
                )}
                <p><span className="text-muted-foreground">Type:</span> <strong>{selectedLeaveForCancel.type || '-'}</strong></p>
                <p><span className="text-muted-foreground">Dates:</span> <span className="font-mono">{selectedLeaveForCancel.startDate || '-'} to {selectedLeaveForCancel.endDate || '-'}</span></p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Comment (optional)</Label>
                <Textarea
                  value={cancelComment}
                  onChange={(e) => setCancelComment(e.target.value)}
                  placeholder="e.g. Travel plans changed"
                  data-testid="cancel-leave-comment"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancellingLeave}>
              Keep Leave
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleCancelLeave}
              disabled={cancellingLeave}
              data-testid="confirm-cancel-leave-btn"
            >
              {cancellingLeave ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
